import { useState } from 'react';
import styles from '../../../styles/agents.module.css';

/**
 * Website Searcher Tool Configuration Component
 * Allows configuration of website search tool with natural language or explicit settings
 */
function WebsiteSearcherToolConfig({ config = {}, onChange, disabled = false }) {
    const [useNaturalLanguage, setUseNaturalLanguage] = useState(
        config.naturalLanguageConfig ? true : false
    );
    const [naturalLanguageConfig, setNaturalLanguageConfig] = useState(
        config.naturalLanguageConfig || ''
    );
    const [searchEngine, setSearchEngine] = useState(config.searchEngine || 'duckduckgo');
    const [maxResults, setMaxResults] = useState(config.maxResults || 10);
    const [domainFilter, setDomainFilter] = useState(config.domainFilter || '');
    const [fileType, setFileType] = useState(config.fileType || '');
    const [dateRange, setDateRange] = useState(config.dateRange || '');

    const handleNaturalLanguageChange = (value) => {
        setNaturalLanguageConfig(value);
        if (onChange) {
            onChange({
                ...config,
                naturalLanguageConfig: value,
            });
        }
    };

    const handleExplicitConfigChange = () => {
        if (onChange) {
            const newConfig = {
                searchEngine,
                maxResults: parseInt(maxResults) || 10,
                domainFilter: domainFilter.trim() || undefined,
                fileType: fileType.trim() || undefined,
                dateRange: dateRange.trim() || undefined,
            };
            // Remove undefined values
            Object.keys(newConfig).forEach(key => {
                if (newConfig[key] === undefined) {
                    delete newConfig[key];
                }
            });
            onChange(newConfig);
        }
    };

    const handleSearchEngineChange = (value) => {
        setSearchEngine(value);
        handleExplicitConfigChange();
    };

    const handleMaxResultsChange = (value) => {
        setMaxResults(value);
        handleExplicitConfigChange();
    };

    const handleDomainFilterChange = (value) => {
        setDomainFilter(value);
        handleExplicitConfigChange();
    };

    const handleFileTypeChange = (value) => {
        setFileType(value);
        handleExplicitConfigChange();
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value);
        handleExplicitConfigChange();
    };

    const handleModeToggle = (useNL) => {
        setUseNaturalLanguage(useNL);
        if (!useNL) {
            handleExplicitConfigChange();
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <input
                        type="radio"
                        checked={!useNaturalLanguage}
                        onChange={() => handleModeToggle(false)}
                        disabled={disabled}
                    />
                    <span>Explicit Configuration</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                        type="radio"
                        checked={useNaturalLanguage}
                        onChange={() => handleModeToggle(true)}
                        disabled={disabled}
                    />
                    <span>Natural Language Configuration</span>
                </label>
            </div>

            {useNaturalLanguage ? (
                <div>
                    <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel}>
                            <strong>Configuration (Natural Language):</strong>
                            <textarea
                                value={naturalLanguageConfig}
                                onChange={(e) => handleNaturalLanguageChange(e.target.value)}
                                disabled={disabled}
                                placeholder='e.g., "search on google, max 5 results, only PDFs from example.com"'
                                rows={3}
                                className={styles.formInput}
                                style={{ marginTop: '5px' }}
                            />
                        </label>
                        <div className={styles.formHelp}>
                            Examples:
                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                                <li>"search on google, max 5 results, only PDFs from example.com"</li>
                                <li>"use duckduckgo, find documents from 2020 to 2024"</li>
                                <li>"search bing, limit to 10 results, domain example.org"</li>
                            </ul>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel}>
                            Search Engine:
                        </label>
                        <select
                            value={searchEngine}
                            onChange={(e) => handleSearchEngineChange(e.target.value)}
                            disabled={disabled}
                            className={styles.formInput}
                        >
                            <option value="duckduckgo">DuckDuckGo (No API key required)</option>
                            <option value="google">Google (API key required)</option>
                            <option value="bing">Bing (API key required)</option>
                        </select>
                    </div>

                    <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel}>
                            Max Results:
                        </label>
                        <input
                            type="number"
                            value={maxResults}
                            onChange={(e) => handleMaxResultsChange(e.target.value)}
                            disabled={disabled}
                            min="1"
                            max="50"
                            className={styles.formInput}
                        />
                        <div className={styles.formHelp}>
                            Maximum number of search results to return (1-50)
                        </div>
                    </div>

                    <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel}>
                            Domain Filter (Optional):
                        </label>
                        <input
                            type="text"
                            value={domainFilter}
                            onChange={(e) => handleDomainFilterChange(e.target.value)}
                            disabled={disabled}
                            placeholder="e.g., example.com"
                            className={styles.formInput}
                        />
                        <div className={styles.formHelp}>
                            Limit search results to a specific domain
                        </div>
                    </div>

                    <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel}>
                            File Type Filter (Optional):
                        </label>
                        <input
                            type="text"
                            value={fileType}
                            onChange={(e) => handleFileTypeChange(e.target.value)}
                            disabled={disabled}
                            placeholder="e.g., pdf, doc"
                            className={styles.formInput}
                        />
                        <div className={styles.formHelp}>
                            Limit search results to specific file types
                        </div>
                    </div>

                    <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel}>
                            Date Range (Optional):
                        </label>
                        <input
                            type="text"
                            value={dateRange}
                            onChange={(e) => handleDateRangeChange(e.target.value)}
                            disabled={disabled}
                            placeholder="e.g., 2020:2024"
                            className={styles.formInput}
                        />
                        <div className={styles.formHelp}>
                            Filter results by date range (format: YYYY:YYYY)
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default WebsiteSearcherToolConfig;
